import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import dateformat from "dateformat";

import styles from './styles.module.scss';
import { fetchStreamChildren, fetchStreams } from "src/redux/actions/streams";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Reveal from "src/components/animation";
import { fetchCollection, fetchCollections, fetchCollectionSection } from "src/redux/actions/collections";
import InfiniteScroll from "src/components/infiniteScroll";
import LinesEllipsis from "react-lines-ellipsis";
import { USER_DEFINED } from "src/constants/collections";
import { useSpring, animated } from "@react-spring/web";
import { Stream } from "src/types/streams";
import { Collection, CollectionSection } from "src/types/collections";
import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/services/file.service";
import useMeasure from "react-use-measure";
import { fetchGroupChildren } from "src/redux/actions/groups";
import { SelectEntityContext } from "../index";
import { useAssetAdoption } from "src/redux/actions/adoption/asset";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";
import userRoleService from "src/services/userRoles.service";
import Text from "src/components/text";


interface EntityBrowserProps {
  className?: string;
  width: number;
  entity?: Stream | Collection | CollectionSection;
  items?: any[];
  selected?: number; 

  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const EntityBrowser: React.FC<EntityBrowserProps> = ({ className, width, items, action, onSelection }) => {
  return (
    <div className={cx(styles.container, className)}>
      {items && items.map((item) => (
        <EntityRow key={item.id} entity={item} width={width} action={action} onSelection={onSelection} />
      ))}
    </div>
  );
}


const StreamsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection, selected }) => {
  const dispatch = useDispatch();
  const { loading, streams } = useSelector((state: any) => {

    const streams = _.get(state, 'hub.streams');
    const filteredStreams = (_.get(streams, 'items') || []).filter((item: any) => {
      return item.canEdit;
    });

    
    return {
      loading: state.hub.streams.loading,
      streams: filteredStreams,
    };
  }, _.isEqual);

  useEffect(() => {
    dispatch(fetchStreams());
  }, []);

  const { trackStreamSelectionViewed } = useStreamsAdoption();

  useEffect(() => {
    if (selected === 0) {
      trackStreamSelectionViewed();
    }
  }, [selected]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        <StreamPlaceholder width={'50%'} />
        <StreamPlaceholder width={'70%'} />
        <StreamPlaceholder width={'60%'} />
        <StreamPlaceholder width={'50%'} />
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser className={className} items={streams} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}

const StreamBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.stream.items[entity!.id!]?.loadingChildren,
      items: state.stream.items[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchStreamChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser className={className} width={width} items={items} action={action} onSelection={onSelection} />
      </InfiniteScroll>
    </div>
  );
}

const GroupBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.group[entity!.id!]?.loadingChildren,
      items: state.group[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchGroupChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser className={className} width={width} items={items} action={action} onSelection={onSelection} />
      </InfiniteScroll>
    </div>
  );
}

const CollectionsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector((state: any) => {
    const collectionState = _.get(state, 'collections');
    const loading = _.get(collectionState, 'loading');
    const pagination = _.get(collectionState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const collections = (_.get(collectionState, 'items') || []).filter((item: any) => {
      return item.canEdit;
    });

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      collections: collections,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    if (userRoleService.canAccessCollection()) {
      fetchCollectionsForPage(0);
    }
  }, []);

  const fetchCollectionsForPage = (page: number) => {

    const request = {
      page: page,
      limit: 20,
      sort: {
        field: 'name',
        order: 'asc'
      }
    };

    dispatch(fetchCollections(request));
  }

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      fetchCollectionsForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          if (_.size(collections) === 0) {
            return (<>
              <CollectionPlaceholder width={'50%'} />
              <CollectionPlaceholder width={'70%'} />
              <CollectionPlaceholder width={'60%'} />
              <CollectionPlaceholder width={'50%'} />
            </>)
          } else {
            return <CollectionPlaceholder width={'70%'} />
          }
        }}
      >
        {_.size(collections) === 0 && (
            <div className={styles.emptyMessage}>
              No collections found
            </div>
        )}
        <EntityBrowser className={className}
          width={width}
          items={collections}
          action={action}
          onSelection={onSelection}
        />
      </InfiniteScroll>
    </div>
  );
}

const CollectionBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const collection = entity as Collection;
  const childrenType = collection.sectionsCount! > 0 ? 'collection_section' : 'asset';

  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    const collection = state.collections.collection[entity!.id!]?.item;
    let items = [];
    if (collection) {
      if (_.size(collection.sections) > 0) {
        items = (collection.sections || []).map((item: any) => {
          return {
            ...item.item,
            entityType: 'collection_section'
          }
        });
      } else if (_.size(collection.assets) > 0) {
        items = (collection.assets || []).map((item: any) => {
          return {
            ...item.asset
          }
        });
      }
    }

    return {
      loading: state.collections.collection[entity!.id!]?.loading,
      items: items,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchCollection(entity.id));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        {childrenType === 'collection_section' && (
          <>
            <CollectionSectionPlaceholder width="50%" />
            <CollectionSectionPlaceholder width="70%" />
            <CollectionSectionPlaceholder width="60%" />
            <CollectionSectionPlaceholder width="50%" />
          </>
        )}

        {childrenType === 'asset' && (
          <>
            {_.times((collection.assetsCount || 3), (index) => (
              <AssetPlaceholder key={index} />
            ))}
          </>
        )}

      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser className={className} items={items} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}


/**
 * Entity Renders
*/

interface EntityRowProps {
  entity: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const Component = useMemo(() => {
    const type = entity?.entityType;
    switch (type) {
      case 'stream':
        return StreamRow;
      case 'group':
        return GroupRow;
      case 'collection':
        return CollectionRow;
      case 'collection_section':
        return CollectionSectionRow;
      default:
        return false;
    }
  }, [entity]);


  if (!Component) {
    return null;
  }

  return (
    React.createElement(Component, { entity, width, action, onSelection })
  );
}

const StreamRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();
  const { selectedEntity } = React.useContext(SelectEntityContext);
  const isEntitySelected = _.isEqual(_.get(selectedEntity, "id"), _.get(entity, "id"));

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    onSelection(entity);

    if(entity?.groupCount > 0) {
      setExpanded(!expanded);
      setMountChildren(true);
    }
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection)} style={{ width: width }} onClick={handleClick}>
        <div className={styles.inner}>
          <Text className={styles.name} text={entity?.name} lines={1} tooltip={{placement: "auto"}}/>

          {entity?.groupCount > 0 && (<button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
          )}
        </div>
        <div className={styles.actions}>
          <button onClick={(e) => {
            e.stopPropagation();
            onSelection(entity);
          }} className={cx(styles.action, styles.selectIcon, {
            [styles.activeSelect]: isEntitySelected
          })}>
            <i
              className={cx(styles.arrow, isEntitySelected ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected'
              )} />
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <StreamBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const GroupRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();
  const { selectedEntity } = React.useContext(SelectEntityContext);
  const isEntitySelected = _.isEqual(_.get(selectedEntity, "id"), _.get(entity, "id"));

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection)} style={{ width: width }} onClick={handleClick}>
        <div className={styles.inner}>
          <Text className={styles.name} text={entity?.name} lines={1} tooltip={{placement: "auto"}} />
        </div>
        <div className={styles.actions}>
          <button onClick={(e) => {
            e.stopPropagation();
            onSelection(entity);
          }} className={cx(styles.action, styles.selectIcon, {
            [styles.activeSelect]: isEntitySelected
          })}>
            <i
              className={cx(styles.arrow, isEntitySelected ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected'
              )} />
          </button>
        </div>
      </div>
      <animated.div style={childrenAnimation}>
        {mountChildren && (
          <animated.div style={childrenAnimation} className={styles.children}>
            <div ref={containerRef} className={styles.inner}>
              <GroupBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
            </div>
          </animated.div>
        )}
      </animated.div>
    </>
  );
}

const CollectionRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {

  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const { selectedEntity, selectedEntities } = React.useContext(SelectEntityContext);
  
  const isEntitySelected = selectedEntities ? selectedEntities?.some(selectedEntity => 
    _.isEqual(_.get(selectedEntity, "id"), _.get(entity, "id"))
  ) : _.isEqual(_.get(selectedEntity, "id"), _.get(entity, "id"));

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    onSelection(entity);
    if(entity?.sectionsCount > 0) {
      setExpanded(!expanded);
      setMountChildren(true);
    }
  }


  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection)} style={{ width: width }} onClick={handleClick}>
        <div className={styles.inner}>
          <Text className={styles.name} text={entity?.name} lines={1} tooltip={{placement: "auto"}} />
          {entity?.sectionsCount > 0 && <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>}
        </div>
        {entity?.sectionsCount === 0 && <div className={styles.actions}>
          <button onClick={(e) => {
            e.stopPropagation();
            onSelection(entity);
          }} className={cx(styles.action, styles.selectIcon, {
            [styles.activeSelect]: isEntitySelected
          })}>
            <i
              className={cx(styles.arrow, isEntitySelected ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected'
              )} />
          </button>
        </div>}
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <CollectionBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const CollectionSectionRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {

  const { selectedEntity, selectedEntities } = React.useContext(SelectEntityContext);
  
  const isEntitySelected = selectedEntities ? selectedEntities?.some(selectedEntity => 
    _.isEqual(_.get(selectedEntity, "id"), _.get(entity, "id"))
  ) : _.isEqual(_.get(selectedEntity, "id"), _.get(entity, "id"));

  const handleClick = () => {
    onSelection(entity);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection)} style={{ width: width }} onClick={handleClick}>
        <div className={styles.inner}>
          <Text className={styles.name} text={entity?.name} lines={1} tooltip={{placement: "auto"}} />
        </div>
        <div className={styles.actions}>
          <button onClick={(e) => {
            e.stopPropagation();
            onSelection(entity);
          }} className={cx(styles.action, styles.selectIcon, {
            [styles.activeSelect]: isEntitySelected
          })}>
            <i
              className={cx(styles.arrow, isEntitySelected ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected'
              )} />
          </button>
        </div>
      </div>
    </>
  );
}

export const StreamPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.placeholderName, styles.block)}></div>
    </div>
  );
}

export const CollectionPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.placeholderName, styles.block)}></div>
    </div>
  );
}

export const CollectionSectionPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.placeholderName, styles.block)}></div>
    </div>
  );
}

export const AssetPlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.entity, styles.asset, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.meta}>
            <span className={styles.block} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { EntityBrowser, StreamsBrowser, CollectionsBrowser };